if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.onDockSelectionChanged = this.onDockSelectionChanged.bind(this);
        this.onFlowPlusSelectionChanged = this.onFlowPlusSelectionChanged.bind(this);
        this.onFlowPlusContentChanged = this.onFlowPlusContentChanged.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        document.addEventListener('flow:dock-selection-changed', this.onDockSelectionChanged);
        this.flowPlusOptionInputs.forEach((input) => input.addEventListener('change', this.onFlowPlusSelectionChanged));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.hasDockSelection = !this.dockSelector || Boolean(this.selectedDockCard);

        this.updateFlowPlusUi();
        this.observeFlowPlusContentEdits();
        this.syncFlowPlusHiddenState();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          this.debugFlowPlus('submit blocked: button is aria-disabled');
          return;
        }

        this.syncFlowPlusHiddenState();
        this.handleErrorMessage();
        this.debugFlowPlus('submit start', {
          flowPlusEnabled: this.flowPlusEnabled,
          flowPlusMode: this.flowPlusMode,
          flowPlusVariantId: this.dataset.flowPlusVariantId,
          flowPlusSellingPlanId: this.dataset.flowPlusSellingPlanId,
          flowPlusChecked: this.flowPlusCheckbox?.checked,
          flowPlusSelectedOffer: this.selectedFlowPlusOffer,
          formVariantId: this.variantIdInput?.value,
        });

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        this.syncAcquisitionOfferSelection();
        const formData = new FormData(this.form);

        this.submitCart(formData, config)
          .then((response) => this.parseCartResponse(response))
          .then((response) => {
            this.debugFlowPlus('cart response parsed', response);

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) {
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.error = false;
            this.debugFlowPlus('redirecting to checkout');
            window.location = '/checkout';
          })
          .catch((e) => {
            this.debugFlowPlus('submit failed', e);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
        }

      submitCart(formData, config) {
        if (this.flowPlusEnabled) {
          return this.submitFlowPlusCart(formData, config);
        }

        this.debugFlowPlus('standard cart submit path');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        return fetch('/cart/clear.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            this.debugFlowPlus('cart clear response', { ok: response.ok, status: response.status });
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => {
            this.debugFlowPlus('cart add response', { ok: response.ok, status: response.status, url: response.url });
            return response;
          });
      }

      submitFlowPlusCart(formData, config) {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const dockSelection = this.getDockSelection(formData);

        if (!dockSelection) {
          this.handleErrorMessage('Choose a dock before checkout.');
          return Promise.reject(new Error('Missing dock selection for checkout'));
        }

        const selectedOffer = this.selectedFlowPlusOffer;
        const checkoutIntentId = selectedOffer ? this.createCheckoutIntentId() : null;

        const dockItem = {
          id: Number(dockSelection.variantId),
          quantity: dockSelection.quantity,
        };

        const items = [dockItem];

        if (selectedOffer && checkoutIntentId) {
          // Only tag the subscription line. Tagging the dock line makes backend
          // grant reconciliation treat both lines as Flow+ membership candidates.
          const flowPlusProperties = {
            _flow_offer_key: selectedOffer.offerKey,
            _flow_checkout_intent_id: checkoutIntentId,
            _flow_membership: 'true',
          };

          items.push({
            id: Number(selectedOffer.variantId),
            quantity: 1,
            selling_plan: Number(selectedOffer.sellingPlanId),
            properties: flowPlusProperties,
          });
        }

        config.body = JSON.stringify({ items });
        this.debugFlowPlus('flow plus cart payload', { items });

        return fetch('/cart/clear.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            this.debugFlowPlus('cart clear response', { ok: response.ok, status: response.status });
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => {
            this.debugFlowPlus('cart add response', { ok: response.ok, status: response.status, url: response.url });
            return response;
          });
      }

      parseCartResponse(response) {
        return response
          .clone()
          .json()
          .catch((error) =>
            response.text().then((text) => {
              this.debugFlowPlus('cart response was not JSON', {
                status: response.status,
                text: text.slice(0, 500),
                error: String(error),
              });
              throw error;
            })
          );
      }

      createCheckoutIntentId() {
        if (window.crypto?.randomUUID) {
          return window.crypto.randomUUID();
        }

        return `flow-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      getDockSelection(formData) {
        if (this.dockSelector) {
          const selectedDockCard = this.selectedDockCard;
          if (!selectedDockCard?.dataset.variantId) return null;

          return {
            variantId: selectedDockCard.dataset.variantId,
            quantity: Number.parseInt(selectedDockCard.dataset.dockQuantity || '1', 10) || 1,
          };
        }

        const variantId = formData.get('id');
        if (!variantId) return null;

        return {
          variantId,
          quantity: Number.parseInt(formData.get('quantity') || '1', 10) || 1,
        };
      }

      onDockSelectionChanged(event) {
        const sectionId = event.detail?.sectionId;
        if (String(sectionId) !== String(this.dataset.sectionId)) return;

        this.hasDockSelection = Boolean(event.detail?.variantId) || Boolean(this.selectedDockCard);
        this.updateFlowPlusUi();
      }

      onFlowPlusSelectionChanged() {
        this.updateFlowPlusUi();
      }

      onFlowPlusContentChanged() {
        this.syncFlowPlusHiddenState();
      }

      syncAcquisitionOfferSelection() {
        const offer = this.closest('[data-acquisition-offer]');
        if (!offer) return;

        const selectedPack = offer.querySelector('[data-offer-pack-input]:checked');
        if (!selectedPack?.dataset.variantId) return;

        if (this.variantIdInput) {
          this.variantIdInput.value = selectedPack.dataset.variantId;
          this.variantIdInput.disabled = selectedPack.dataset.available === 'false';
        }

        const quantityInput = this.form.querySelector('[name="quantity"]');
        if (quantityInput) {
          quantityInput.value = selectedPack.dataset.quantity || '1';
        }
      }

      observeFlowPlusContentEdits() {
        if (!this.flowPlusCheckbox || typeof MutationObserver === 'undefined') return;

        this.flowPlusContentObserver = new MutationObserver(this.onFlowPlusContentChanged);
        this.flowPlusContentObserver.observe(this, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
          childList: true,
          subtree: true,
        });
      }

      syncFlowPlusHiddenState() {
        const checkbox = this.flowPlusCheckbox;
        if (!checkbox || !this.isFlowPlusLabelHidden() || !checkbox.checked) return;

        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        this.debugFlowPlus('unchecked hidden Flow+ offer');
      }

      isFlowPlusLabelHidden() {
        const label = this.flowPlusLabel;
        if (!label) return true;

        let element = label;
        while (element && element !== this) {
          if (element.hidden || element.getAttribute('aria-hidden') === 'true') return true;

          const styles = window.getComputedStyle(element);
          if (styles.display === 'none' || styles.visibility === 'hidden') return true;

          element = element.parentElement;
        }

        return false;
      }

      updateFlowPlusUi() {
        const helper = this.querySelector('[data-flow-plus-helper]');
        const offer = this.querySelector('[data-flow-plus-offer]');

        if (helper && offer) {
          const hasSelection = this.hasDockSelection || Boolean(this.selectedFlowPlusOffer);
          helper.textContent = hasSelection
            ? offer.dataset.flowPlusReadyHelper || ''
            : 'Choose Flow+, a dock, or both';
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get productInfoContainer() {
        return document.getElementById(`ProductInfo-${this.dataset.sectionId}`);
      }

      get dockSelector() {
        const selectors = [
          ...(this.productInfoContainer?.querySelectorAll(
            '.dock-selector[data-section-id], [data-grunz-buy-box][data-section-id]'
          ) || []),
        ];
        return selectors.find((selector) => this.isElementVisible(selector)) || selectors[0] || null;
      }

      get selectedDockCard() {
        return this.dockSelector?.querySelector('.is-active[data-variant-id]') || null;
      }

      isElementVisible(element) {
        return Boolean(element?.offsetParent);
      }

      get flowPlusEnabled() {
        if (this.dataset.flowPlusEnabled !== 'true') return false;

        if (this.flowPlusMode === 'selector') {
          return this.flowPlusOptionInputs.length > 0;
        }

        return Boolean(this.dataset.flowPlusVariantId) && Boolean(this.dataset.flowPlusSellingPlanId);
      }

      get flowPlusCheckbox() {
        return this.querySelector('[data-flow-plus-checkbox]');
      }

      get flowPlusLabel() {
        return this.querySelector('.flow-plus-offer__label');
      }

      get flowPlusSelector() {
        return this.querySelector('[data-flow-plus-selector]');
      }

      get flowPlusOptionInputs() {
        return [...this.querySelectorAll('[data-flow-plus-option-input]')];
      }

      get flowPlusMode() {
        return this.dataset.flowPlusMode || 'single';
      }

      get flowPlusDockRequired() {
        return this.dataset.flowPlusDockRequired === 'true';
      }

      get selectedFlowPlusOffer() {
        if (this.flowPlusMode === 'selector') {
          const selectedInput = this.querySelector('[data-flow-plus-option-input]:checked');

          if (!selectedInput?.dataset.variantId || !selectedInput?.dataset.sellingPlanId) {
            return null;
          }

          return {
            variantId: selectedInput.dataset.variantId,
            sellingPlanId: selectedInput.dataset.sellingPlanId,
            offerKey: selectedInput.dataset.offerKey || 'dock_plus_flow_web_v1',
          };
        }

        if (!this.flowPlusCheckbox?.checked) {
          return null;
        }

        return {
          variantId: this.dataset.flowPlusVariantId,
          sellingPlanId: this.dataset.flowPlusSellingPlanId,
          offerKey: this.dataset.flowPlusOfferKey || 'dock_plus_flow_web_v1',
        };
      }

      get flowPlusDebugEnabled() {
        try {
          return (
            new URLSearchParams(window.location.search).has('flow_debug') ||
            window.localStorage?.getItem('flow_plus_debug') === 'true'
          );
        } catch (error) {
          return false;
        }
      }

      debugFlowPlus(message, payload) {
        if (!this.flowPlusDebugEnabled) return;

        if (payload === undefined) {
          console.debug('[Flow+ checkout]', message);
        } else {
          console.debug('[Flow+ checkout]', message, payload);
        }
      }
    }
  );
}
